import React, { useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { MetricKey, ReportResponse } from "../api/types";
import { formatMetric, METRIC_LABELS } from "../shared/format";
import { METRIC_DEFINITIONS, buildMethodologyCsvRows } from "../shared/metricDefinitions";
import { SecondaryButton } from "../../../components/Button";

interface MetricGridProps {
  data: ReportResponse;
  // Header for the dimension/label column (e.g. "Event", "Vendor", "Item", "Date").
  keyColumnHeader: string;
  // When true, show the row's `group` (e.g. an item's Category) as its own column.
  groupColumnHeader?: string;
  // Optional override of the label renderer (e.g. format a day key as a date).
  formatLabel?: (label: string) => string;
  exportName?: string;
  // Extra CSV lines appended after the methodology block (e.g. a summary total the grid has no column for).
  csvExtra?: string[];
  // Per-report column-header overrides (e.g. OrderCount -> "Times Used" on the discounts report).
  metricLabelOverrides?: Partial<Record<MetricKey, string>>;
  // Grid body height (CSS). Default "60vh"; stacked sections pass something shorter.
  height?: string;
}

interface GridRow {
  label: string;
  group: string | null;
  isTotal: boolean;
  [metric: string]: unknown;
}

const TOTAL_LABEL = "Total";

// Renders any KeyedRollup / Composite / TimeSeries response as a table: a label column,
// an optional group column, and one money/count column per metric in `data.metrics`.
// The bold Total row is pinned to the bottom and comes straight from `data.totals`,
// mirroring the old reports' pinnedBottomRowData pattern.
export default function MetricGrid({
  data,
  keyColumnHeader,
  groupColumnHeader,
  formatLabel,
  exportName,
  csvExtra,
  metricLabelOverrides,
  height = "60vh",
}: MetricGridProps) {
  const gridRef = useRef<GridApi>();

  const rows: GridRow[] = useMemo(
    () =>
      data.rows.map((r) => ({
        label: formatLabel ? formatLabel(r.label) : r.label,
        group: r.group,
        isTotal: false,
        ...r.values,
      })),
    [data, formatLabel]
  );

  const totalRow: GridRow[] = useMemo(
    () => [{ label: TOTAL_LABEL, group: null, isTotal: true, ...data.totals }],
    [data]
  );

  const colDefs: ColDef<GridRow>[] = useMemo(() => {
    const boldIfTotal = (p: { data?: GridRow }) =>
      p.data?.isTotal ? { fontWeight: "bold" as const } : undefined;

    const cols: ColDef<GridRow>[] = [
      {
        headerName: keyColumnHeader,
        field: "label",
        pinned: "left",
        filter: "agTextColumnFilter",
        cellStyle: boldIfTotal,
      },
    ];

    if (groupColumnHeader) {
      cols.push({
        headerName: groupColumnHeader,
        field: "group",
        filter: "agTextColumnFilter",
        cellStyle: boldIfTotal,
      });
    }

    for (const metric of data.metrics) {
      const def = METRIC_DEFINITIONS[metric];
      cols.push({
        headerName: metricLabelOverrides?.[metric] ?? METRIC_LABELS[metric] ?? metric,
        // Native header tooltip with the exact formula (hover the column header).
        headerTooltip: def ? `${def.label} = ${def.formula}` : undefined,
        valueGetter: (p) => (p.data ? (p.data[metric] as number | undefined) ?? 0 : 0),
        valueFormatter: (p) => formatMetric(metric, p.value as number),
        type: "rightAligned",
        cellStyle: boldIfTotal,
      });
    }

    return cols;
  }, [data.metrics, keyColumnHeader, groupColumnHeader]);

  const autoSizeStrategy: SizeColumnsToContentStrategy = { type: "fitCellContents" };

  const handleExport = () => {
    gridRef.current?.exportDataAsCsv({
      fileName: `${exportName ?? data.type}_new_${data.window.from}_to_${data.window.to}.csv`,
      allColumns: true,
      // Self-documenting export: the methodology/legend block sits at the top of the file.
      prependContent: [
        ...buildMethodologyCsvRows(data.metrics, data.window),
        ...(csvExtra ?? []).map((line) => [{ data: { value: line } }]),
      ],
    });
  };

  return (
    <>
      <div className="mb-2 flex">
        <div className="ml-auto">
          <SecondaryButton onClick={handleExport}>Export to CSV</SecondaryButton>
        </div>
      </div>
      <div style={{ height }}>
        <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
          <AgGridReact<GridRow>
            rowData={rows}
            columnDefs={colDefs}
            rowHeight={44}
            autoSizeStrategy={autoSizeStrategy}
            pagination
            paginationPageSize={100}
            pinnedBottomRowData={totalRow}
            enableCellTextSelection
            onGridReady={(p) => (gridRef.current = p.api)}
          />
        </div>
      </div>
    </>
  );
}
